import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

const ENDPOINT_URL = '/ord-orders/store';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type CreateOrderSuccessResponse =  paths[url]['post']['responses']['201']['content']['application/json'];

/**
 * Requests endpoint to create a order.
 */
export const useCreateOrder = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setCreatedOrder } = useData();

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const request = async () => {
        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);
        setCreatedOrder(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(getCommonParameters()),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                
                throw error;
            }

            const data = await response.json();

            setSuccess(data.message);
            setCreatedOrder(data as CreateOrderSuccessResponse);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, errorMessage, lastResponseTime };
};
